/**
 * useBulkTest — Supabase-native bulk test hook.
 * Creates synthetic contacts in Supabase and runs agent.execute() with integration
 * payload so agentfactory saves messages + runs the eval pipeline.
 * No Telerivet.
 */

import { useState, useCallback } from "react"
import { TestCase, UseBulkTestResult } from "../types"
import { parseCSV, generateCSV, generateFakeName, delay } from "../utils"
import { agents } from "@/lib/agents"
import { app } from "@/lib/app"
import { supabase } from "@/lib/data"
import { ulid } from "ulid"
import { ensureSyntheticContact } from "./useSupabaseSimulation"

export function useBulkTest(): UseBulkTestResult {
  const [testCases, setTestCases] = useState<TestCase[]>([])
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)

  const addTestCase = useCallback((testCase: Omit<TestCase, "id" | "status">) => {
    setTestCases(prev => [...prev, { ...testCase, id: crypto.randomUUID(), status: "pending" }])
  }, [])

  const removeTestCase = useCallback((id: string) => {
    setTestCases(prev => prev.filter(tc => tc.id !== id))
  }, [])

  const updateTestCase = useCallback((id: string, updates: Partial<TestCase>) => {
    setTestCases(prev => prev.map(tc => tc.id === id ? { ...tc, ...updates } : tc))
  }, [])

  const runAllTests = useCallback(async (routeId?: string, agentId?: string, teamId?: string) => {
    if (!agentId || !teamId) return

    setRunning(true)
    setProgress(0)

    const pendingTests = testCases.filter(tc => tc.status === "pending")
    const total = pendingTests.length

    let apiKeys: any = {}
    try {
      apiKeys = await app.fetchAPIkeys(teamId)
    } catch {
      /* keys missing — agent may surface error */
    }

    for (let i = 0; i < pendingTests.length; i++) {
      const testCase = pendingTests[i]
      updateTestCase(testCase.id, { status: "sending" })

      try {
        const contactId = await ensureSyntheticContact(null, teamId, testCase.label)

        const agentInstance = await agents.loadAgent(agentId as any, teamId)
        if (!agentInstance) throw new Error("Agent not found")

        const p: AgentParameters = {
          debug: false,
          uid: contactId,
          team: teamId,
          input: { message: testCase.message },
          apiKeys,
          // No integration — we write messages directly below
        }

        // Write user message
        const agentIdNum = agentId ? (parseInt(agentId, 10) || null) : null
        await supabase.from("messages").insert({
          contact: contactId,
          team: teamId,
          role: "user",
          message: testCase.message,
          channel: "app",
          agent: agentIdNum,
        })

        await agentInstance.execute(p)
        if (p.error) throw new Error(p.error)

        const botResponse = p.output?.response || ""

        // Write agent response
        if (botResponse) {
          await supabase.from("messages").insert({
            contact: contactId,
            team: teamId,
            role: "assistant",
            message: botResponse,
            channel: "app",
            agent: agentIdNum,
          })
        }

        updateTestCase(testCase.id, {
          status: "sent",
          response: botResponse || contactId,
          contactId,
          timestamp: Date.now(),
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to run test"
        updateTestCase(testCase.id, {
          status: "error",
          error: errorMessage,
          timestamp: Date.now(),
        })
      }

      setProgress(((i + 1) / total) * 100)

      // Rate-limit to avoid hammering the API
      if (i < pendingTests.length - 1) {
        await delay(500)
      }
    }

    setRunning(false)
  }, [testCases, updateTestCase])

  const importFromCSV = useCallback((csv: string) => {
    const parsed = parseCSV(csv)
    setTestCases(parsed.map(item => ({
      id: crypto.randomUUID(),
      label: item.label,
      message: item.message,
      systemPrompt: item.systemPrompt,
      status: "pending" as const,
    })))
  }, [])

  const generateCSVExport = useCallback(() => {
    return generateCSV(testCases.map(tc => ({
      label: tc.label,
      message: tc.message,
      systemPrompt: tc.systemPrompt,
    })))
  }, [testCases])

  const clear = useCallback(() => {
    setTestCases([])
    setProgress(0)
  }, [])

  return {
    testCases,
    running,
    progress,
    addTestCase,
    removeTestCase,
    updateTestCase,
    runAllTests,
    importFromCSV,
    generateCSV: generateCSVExport,
    clear,
  }
}
